import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { FaArrowLeft, FaMagic, FaPlus, FaCheck, FaTrash, FaImage } from "react-icons/fa";
import { API_BASE_URL } from "../../config/apiConfig";
import { MathText, MathJaxContext, mathJaxConfig } from "../../components/MathText";

const EMPTY_FORM = {
    subject: "", class: "", topic: "", level: "Medium",
    question: "", option_a: "", option_b: "", option_c: "", option_d: "",
    correctAnswer: "A", explanation: "", question_type: "mcq",
};

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem("token")}` });

const TeacherQuestionBuilder = () => {
    const navigate = useNavigate();
    const [tab, setTab] = useState("manual"); // manual | ai | mine
    const [form, setForm] = useState(EMPTY_FORM);
    const [image, setImage] = useState(null);
    const [saving, setSaving] = useState(false);

    const [aiForm, setAiForm] = useState({ subject: "", topic: "", class: "", level: "Medium", count: 5, lessonNote: "" });
    const [generating, setGenerating] = useState(false);

    const [questions, setQuestions] = useState([]);
    const [filter, setFilter] = useState("draft");
    const [loadingMine, setLoadingMine] = useState(false);

    const fetchMine = async (reviewStatus) => {
        setLoadingMine(true);
        try {
            const res = await fetch(`${API_BASE_URL}/api/teacher-questions/mine?review_status=${reviewStatus}`, {
                headers: authHeaders(),
            });
            const data = await res.json();
            setQuestions(data.data || []);
        } catch (err) {
            toast.error("Could not load your questions");
        } finally {
            setLoadingMine(false);
        }
    };

    useEffect(() => {
        if (tab === "mine") fetchMine(filter);
    }, [tab, filter]);

    const handleManualSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            const body = new FormData();
            Object.entries(form).forEach(([key, value]) => body.append(key, value));
            if (image) body.append("question_image", image);

            const res = await fetch(`${API_BASE_URL}/api/teacher-questions`, {
                method: "POST",
                headers: authHeaders(),
                body,
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.message || "Failed to create question");
            toast.success("Question created");
            setForm(EMPTY_FORM);
            setImage(null);
        } catch (err) {
            toast.error(err.message);
        } finally {
            setSaving(false);
        }
    };

    const handleGenerate = async (e) => {
        e.preventDefault();
        setGenerating(true);
        try {
            const res = await fetch(`${API_BASE_URL}/api/teacher-questions/generate`, {
                method: "POST",
                headers: { "Content-Type": "application/json", ...authHeaders() },
                body: JSON.stringify(aiForm),
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.message || "Failed to generate questions");
            toast.success(`${data.data.length} draft question(s) generated — review them under "My Questions"`);
            setTab("mine");
            setFilter("draft");
        } catch (err) {
            toast.error(err.message);
        } finally {
            setGenerating(false);
        }
    };

    const approveQuestion = async (id) => {
        try {
            const res = await fetch(`${API_BASE_URL}/api/teacher-questions/${id}/approve`, {
                method: "PATCH",
                headers: authHeaders(),
            });
            if (!res.ok) throw new Error("Failed to approve");
            toast.success("Question approved — now visible to students");
            fetchMine(filter);
        } catch (err) {
            toast.error(err.message);
        }
    };

    const deleteQuestion = async (id) => {
        if (!window.confirm("Delete this question?")) return;
        try {
            const res = await fetch(`${API_BASE_URL}/api/teacher-questions/${id}`, {
                method: "DELETE",
                headers: authHeaders(),
            });
            if (!res.ok) throw new Error("Failed to delete");
            toast.success("Deleted");
            fetchMine(filter);
        } catch (err) {
            toast.error(err.message);
        }
    };

    return (
        <MathJaxContext config={mathJaxConfig}>
            <div className="min-h-screen bg-gray-50">
                <nav className="bg-[#2148C0] shadow-md px-6 py-4 flex items-center gap-4">
                    <button onClick={() => navigate("/teacher-dashboard")} className="text-white">
                        <FaArrowLeft size={18} />
                    </button>
                    <h1 className="text-xl font-bold text-white">Question Builder</h1>
                </nav>

                <div className="max-w-4xl mx-auto p-6">
                    <div className="flex gap-2 mb-6 bg-white p-1 rounded-lg shadow-sm w-fit">
                        {[
                            { id: "manual", label: "Write a Question" },
                            { id: "ai", label: "Generate with AI" },
                            { id: "mine", label: "My Questions" },
                        ].map((t) => (
                            <button
                                key={t.id}
                                onClick={() => setTab(t.id)}
                                className={`px-4 py-2 rounded-md text-sm font-semibold transition ${
                                    tab === t.id ? "bg-blue600 text-white" : "text-gray-600 hover:bg-gray-100"
                                }`}
                            >
                                {t.label}
                            </button>
                        ))}
                    </div>

                    {tab === "manual" && (
                        <form onSubmit={handleManualSubmit} className="bg-white rounded-xl shadow-sm p-6 space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                                <input required placeholder="Subject" className="border rounded-lg p-2" value={form.subject}
                                    onChange={(e) => setForm({ ...form, subject: e.target.value })} />
                                <input required placeholder="Class (e.g. SS2)" className="border rounded-lg p-2" value={form.class}
                                    onChange={(e) => setForm({ ...form, class: e.target.value })} />
                                <input required placeholder="Topic" className="border rounded-lg p-2" value={form.topic}
                                    onChange={(e) => setForm({ ...form, topic: e.target.value })} />
                                <select className="border rounded-lg p-2" value={form.level}
                                    onChange={(e) => setForm({ ...form, level: e.target.value })}>
                                    <option>Easy</option><option>Medium</option><option>Hard</option>
                                </select>
                            </div>

                            <select className="border rounded-lg p-2" value={form.question_type}
                                onChange={(e) => setForm({ ...form, question_type: e.target.value })}>
                                <option value="mcq">Multiple choice</option>
                                <option value="theory">Theory</option>
                            </select>

                            <textarea required placeholder="Write the question in plain text (avoid $ or LaTeX symbols — e.g. write x^2 instead of $x^2$)"
                                className="border rounded-lg p-2 w-full" rows={3} value={form.question}
                                onChange={(e) => setForm({ ...form, question: e.target.value })} />

                            <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer w-fit border rounded-lg px-3 py-2">
                                <FaImage /> {image ? image.name : "Attach an image (optional)"}
                                <input type="file" accept="image/*" className="hidden" onChange={(e) => setImage(e.target.files[0])} />
                            </label>

                            {form.question_type === "mcq" && (
                                <div className="grid grid-cols-2 gap-4">
                                    {["A", "B", "C", "D"].map((letter) => (
                                        <input key={letter} required placeholder={`Option ${letter}`} className="border rounded-lg p-2"
                                            value={form[`option_${letter.toLowerCase()}`]}
                                            onChange={(e) => setForm({ ...form, [`option_${letter.toLowerCase()}`]: e.target.value })} />
                                    ))}
                                </div>
                            )}

                            {form.question_type === "mcq" && (
                                <select className="border rounded-lg p-2" value={form.correctAnswer}
                                    onChange={(e) => setForm({ ...form, correctAnswer: e.target.value })}>
                                    {["A", "B", "C", "D"].map((l) => <option key={l}>{l}</option>)}
                                </select>
                            )}

                            <textarea placeholder="Explanation / model answer (optional)" className="border rounded-lg p-2 w-full" rows={2}
                                value={form.explanation} onChange={(e) => setForm({ ...form, explanation: e.target.value })} />

                            <button disabled={saving} className="bg-blue600 text-white px-5 py-2 rounded-lg font-semibold flex items-center gap-2 disabled:opacity-50">
                                <FaPlus /> {saving ? "Saving..." : "Add Question"}
                            </button>
                        </form>
                    )}

                    {tab === "ai" && (
                        <form onSubmit={handleGenerate} className="bg-white rounded-xl shadow-sm p-6 space-y-4">
                            <p className="text-sm text-gray-500">
                                Gemini will draft plain-text questions (no LaTeX/math symbols) — review and approve each one before students see it.
                            </p>
                            <div className="grid grid-cols-2 gap-4">
                                <input required placeholder="Subject" className="border rounded-lg p-2" value={aiForm.subject}
                                    onChange={(e) => setAiForm({ ...aiForm, subject: e.target.value })} />
                                <input required placeholder="Topic" className="border rounded-lg p-2" value={aiForm.topic}
                                    onChange={(e) => setAiForm({ ...aiForm, topic: e.target.value })} />
                                <input required placeholder="Class (e.g. SS2)" className="border rounded-lg p-2" value={aiForm.class}
                                    onChange={(e) => setAiForm({ ...aiForm, class: e.target.value })} />
                                <select className="border rounded-lg p-2" value={aiForm.level}
                                    onChange={(e) => setAiForm({ ...aiForm, level: e.target.value })}>
                                    <option>Easy</option><option>Medium</option><option>Hard</option>
                                </select>
                                <input type="number" min={1} max={20} placeholder="How many?" className="border rounded-lg p-2" value={aiForm.count}
                                    onChange={(e) => setAiForm({ ...aiForm, count: e.target.value })} />
                            </div>
                            <textarea placeholder="Optional: paste a lesson note to base the questions on" className="border rounded-lg p-2 w-full" rows={4}
                                value={aiForm.lessonNote} onChange={(e) => setAiForm({ ...aiForm, lessonNote: e.target.value })} />
                            <button disabled={generating} className="bg-blue600 text-white px-5 py-2 rounded-lg font-semibold flex items-center gap-2 disabled:opacity-50">
                                <FaMagic /> {generating ? "Generating..." : "Generate Questions"}
                            </button>
                        </form>
                    )}

                    {tab === "mine" && (
                        <div>
                            <div className="flex gap-2 mb-4">
                                {["draft", "approved"].map((f) => (
                                    <button key={f} onClick={() => setFilter(f)}
                                        className={`px-3 py-1 rounded-full text-sm font-medium ${filter === f ? "bg-blue600 text-white" : "bg-white text-gray-600 border"}`}>
                                        {f === "draft" ? "Pending review" : "Approved"}
                                    </button>
                                ))}
                            </div>

                            {loadingMine && <p className="text-gray-500">Loading...</p>}
                            {!loadingMine && questions.length === 0 && (
                                <p className="text-gray-500">No {filter} questions yet.</p>
                            )}

                            <div className="space-y-3">
                                {questions.map((q) => (
                                    <div key={q.id} className="bg-white rounded-xl shadow-sm p-4">
                                        <div className="flex justify-between items-start gap-4">
                                            <div className="flex-1">
                                                <div className="text-xs text-gray-400 mb-1">{q.subject} · {q.topic} · {q.source}</div>
                                                <MathText text={q.question} className="font-medium text-gray-800" />
                                                {q.image && <img src={q.image} alt="question" className="mt-2 max-h-40 rounded-lg" />}
                                                {q.question_type === "mcq" && (
                                                    <ul className="mt-2 text-sm text-gray-600 space-y-1">
                                                        {["A", "B", "C", "D"].map((l) => (
                                                            <li key={l} className={q.correctAnswer === l ? "text-green-600 font-semibold" : ""}>
                                                                {l}. <MathText text={q[`option_${l.toLowerCase()}`]} />
                                                            </li>
                                                        ))}
                                                    </ul>
                                                )}
                                            </div>
                                            <div className="flex flex-col gap-2">
                                                {q.review_status === "draft" && (
                                                    <button onClick={() => approveQuestion(q.id)} className="text-green-600 hover:bg-green-50 p-2 rounded-lg" title="Approve">
                                                        <FaCheck />
                                                    </button>
                                                )}
                                                <button onClick={() => deleteQuestion(q.id)} className="text-red-500 hover:bg-red-50 p-2 rounded-lg" title="Delete">
                                                    <FaTrash />
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </MathJaxContext>
    );
};

export default TeacherQuestionBuilder;
